'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface InvestmentSpecial {
  id: string;
  title: string;
  category: string;
  minInvestment: string;
  targetRoi: string;
  roiPercentage: number;
  availability: 'limited' | 'open' | 'closing-soon';
  image: string;
  alt: string;
  riskLevel: 'low' | 'medium' | 'high';
  duration: string;
  spotsAvailable: number;
  highlights: string[];
  deadline: string;
  description: string;
  terms: string[];
  contactInfo: string;
}

interface InvestmentSpecialModalProps {
  special: InvestmentSpecial | null;
  isOpen: boolean;
  onClose: () => void;
}

const InvestmentSpecialModal = ({ special, isOpen, onClose }: InvestmentSpecialModalProps) => {
  const { t } = useLanguage();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !special) return null;

  const riskConfig = {
    'low': { color: 'text-success', bg: 'bg-success/10', text: t('investmentCard.lowRisk') },
    'medium': { color: 'text-warning', bg: 'bg-warning/10', text: t('investmentCard.mediumRisk') },
    'high': { color: 'text-destructive', bg: 'bg-destructive/10', text: t('investmentCard.highRisk') },
  };

  const currentRisk = riskConfig[special.riskLevel];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border animate-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-heading font-bold text-foreground">{t('modal.investmentOpportunityDetails')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
            aria-label={t('modal.closeModal')}
          >
            <Icon name="XMarkIcon" size={24} variant="outline" className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <AppImage
              src={special.image}
              alt={special.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
              {special.category}
            </div>
          </div>

          <h3 className="text-3xl font-heading font-bold text-foreground mb-4">{special.title}</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.minInvestment')}</p>
              <p className="text-xl font-heading font-bold text-foreground">{special.minInvestment}</p>
            </div>
            <div className="bg-primary/10 rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.targetROI')}</p>
              <p className="text-xl font-heading font-bold text-primary">{special.targetRoi}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.duration')}</p>
              <p className="text-xl font-heading font-bold text-foreground">{special.duration}</p>
            </div>
            <div className={`${currentRisk.bg} rounded-lg p-4`}>
              <p className="text-xs text-muted-foreground mb-1">{t('investmentCard.riskLevel')}</p>
              <p className={`text-xl font-heading font-bold ${currentRisk.color}`}>{currentRisk.text}</p>
            </div>
          </div>

          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <Icon name="ClockIcon" size={24} variant="solid" className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-foreground mb-1">{t('investmentCard.limitedTimeOpportunity')}</p>
              <p className="text-sm text-muted-foreground">{t('investmentCard.applicationDeadline')}: <span className="font-bold text-warning">{special.deadline}</span> • {t('investmentCard.only')} <span className="font-bold">{special.spotsAvailable} {t('investmentCard.spotsRemaining')}</span></p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3">{t('modal.investmentOverview')}</h4>
            <p className="text-muted-foreground leading-relaxed">{special.description}</p>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3">{t('common.keyHighlights')}</h4>
            <ul className="space-y-2">
              {special.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3">{t('modal.investmentTerms')}</h4>
            <ul className="space-y-2">
              {special.terms.map((term, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{term}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-heading font-bold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary" />
              <span>Ready to Invest?</span>
            </h4>
            <p className="text-muted-foreground mb-4">
              Our investment team is ready to guide you through the process and answer any questions you may have about this opportunity.
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{t('investmentCard.contact')}:</span> {special.contactInfo}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors duration-300"
            >
              {t('common.close')}
            </button>
            <button
              className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors duration-300 flex items-center justify-center space-x-2 shadow-md"
            >
              <Icon name="PhoneIcon" size={20} variant="outline" />
              <span>{t('modal.scheduleConsultation')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentSpecialModal;